import { useMemo, useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { TrendingUp } from "lucide-react";

const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const praticaDate = (p) => {
  const d = p.created_at ? new Date(p.created_at) : null;
  if (d && !isNaN(d)) return d;
  const m = (p.numero_pratica || "").match(/^(\d{4})/);
  return m ? new Date(Number(m[1]), 0, 1) : null;
};

const bucket = (d, period) => {
  const y = d.getFullYear(), m = d.getMonth();
  if (period === "anno") return { key: `${y}`, label: `${y}` };
  if (period === "trimestre") { const q = Math.floor(m / 3) + 1; return { key: `${y}-${q}`, label: `T${q} ${y}` }; }
  return { key: `${y}-${String(m + 1).padStart(2, "0")}`, label: `${MESI[m]} ${String(y).slice(2)}` };
};

// Andamento nel tempo delle pratiche segnalate da un segnalatore.
export default function SegnalatoreTrendChart({ nome, pratiche }) {
  const [period, setPeriod] = useState("mese");
  const data = useMemo(() => {
    const map = new Map();
    for (const p of pratiche) {
      const d = praticaDate(p);
      if (!d) continue;
      const b = bucket(d, period);
      const r = map.get(b.key) || { key: b.key, label: b.label, aperte: 0, chiuse: 0, totale: 0 };
      if (p.stato === "chiusa") r.chiuse += 1; else r.aperte += 1;
      r.totale += 1;
      map.set(b.key, r);
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [pratiche, period]);

  const tot = pratiche.length;
  const chiuse = pratiche.filter((p) => p.stato === "chiusa").length;
  const media = data.length ? (tot / data.length).toFixed(1).replace(".", ",") : "0";

  return (
    <div className="pgp-card p-4 mb-4 pgp-fade-up" data-testid="seg-trend-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-amber-400" />
          <div>
            <div className="text-sm font-semibold text-slate-100">Rendimento segnalatore: <span className="text-amber-400" data-testid="seg-trend-nome">{nome}</span></div>
            <div className="text-xs text-slate-400">
              Totale <b className="text-slate-200" data-testid="seg-trend-totale">{tot}</b> · chiuse <b className="text-slate-200">{chiuse}</b> · media <b className="text-slate-200">{media}</b> per {period}
            </div>
          </div>
        </div>
        <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid="seg-trend-period">
          {[["mese", "Mese"], ["trimestre", "Trimestre"], ["anno", "Anno"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setPeriod(v)} data-testid={`seg-trend-${v}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${period === v ? "bg-amber-500 text-[#0a0d14]" : "text-slate-300 hover:bg-slate-800"}`}>{l}</button>
          ))}
        </div>
      </div>
      {data.length === 0 ? <div className="text-center text-sm text-slate-500 py-8">Nessuna pratica per questo segnalatore.</div> : (
        <div className="h-64" data-testid="seg-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.4} />
              <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fill: "#94a3b8", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#121824", border: "1px solid #334155", borderRadius: 8, color: "#e2e8f0" }} cursor={{ fill: "rgba(245,158,11,0.08)" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="aperte" name="Aperte / in corso" stackId="a" fill="#f59e0b" />
              <Bar dataKey="chiuse" name="Chiuse" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
